/**
 * useContentBlocks — one-time public CMS fetch, shared across every
 * component that needs a key. Backed by `GET /api/public/cms/content-blocks`.
 *
 * Why module-scope cache: content-blocks change rarely (admins tweak
 * them via the CMS on the order of days/weeks), and every visitor-
 * facing consumer needs the same map. Making it per-component state
 * would cause N fetches per page load; making it context/provider would
 * force every consumer to be a child of that provider. A module-scope
 * lazy fetch keeps the API neat (`const blocks = useContentBlocks()`)
 * without provider plumbing.
 *
 * Consumers:
 *   • components/AnnouncementBar.jsx (site.announcement.*)
 *   • Future: homepage.hero.*, footer.*, etc. — piggy-back on this fetch.
 *
 * Failure mode: on network error we fall back to `{}` (empty map). Every
 * consumer already guards with `.trim()`/truthy checks and renders
 * nothing when the key is absent, so CMS being down is a silent no-op
 * rather than a visible error surface.
 */
import { useEffect, useState } from "react";
import axios from "axios";

let _cache = null;
let _pending = null;

async function _fetchBlocks() {
  if (_cache) return _cache;
  if (_pending) return _pending;
  const API =
    (typeof window !== "undefined" && window.location && window.location.origin)
      ? window.location.origin
      : process.env.REACT_APP_BACKEND_URL;
  _pending = axios
    .get(`${API}/api/public/cms/content-blocks`)
    .then((r) => {
      _cache = r?.data?.blocks || {};
      return _cache;
    })
    .catch(() => {
      _cache = {};
      return _cache;
    })
    .finally(() => {
      _pending = null;
    });
  return _pending;
}

export function useContentBlocks() {
  const [blocks, setBlocks] = useState(_cache);
  useEffect(() => {
    if (_cache) return; // Already resolved — nothing to do.
    let cancelled = false;
    _fetchBlocks().then((b) => {
      if (!cancelled) setBlocks(b);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return blocks || {};
}
